import { FormHandlers, useNotifications } from '@doveit/bricks';
import React from 'react';
import {
  JobApplicationStatus, JobApplicationType, OptInSource, OptInType,
} from '../../../domain/types';
import useRBAC from '../../../hooks/use-rbac/useRBAC';
import { createApplicant } from '../../../providers/api/applicant/applicantProvider';
import {
  Applicant, JobApplication,
} from '../../../providers/api/dtos';
import { createJobApplication } from '../../../providers/api/job-application/jobApplicationProvider';
import { booleanStringToBool } from '../../../utils/boolean-string/booleanString';
import { UserRole } from '../../../types';
import CreateJobApplicationForm from '../../components/create-job-application-form/CreateJobApplicationForm';
import { CreateJobApplicationFormModel } from '../../types';
import { JobApplicationSource } from '../../../domain/types/jobApplicationSource';

export interface CreateJobApplicationWithApplicantProps {
  onSuccess?: (applicant: Applicant, jobApplication: JobApplication) => void,
  formRef?: React.MutableRefObject<FormHandlers>,
}

export const JOB_POSITION_LOAD_ERROR = 'Non è stato possibile caricare il ruolo';
export const JOB_APPLICATION_WITH_APPLICANT_SUCCESS_MESSAGE = 'Candidatura aggiunta correttamente. <a href="__LINK__" target="_blank">Visualizza</a>';
export const JOB_APPLICATION_WITH_APPLICANT_ERROR_MESSAGE = 'Impossibile aggiungere la candidatura';

const CreateJobApplicationWithApplicant: React.FC<CreateJobApplicationWithApplicantProps> = ({
  onSuccess,
  formRef,
}) => {
  const [isSaving, setIsSaving] = React.useState(false);
  const { user, mainUserRole } = useRBAC();
  const { addSuccess, addError } = useNotifications();

  const initialValues: Partial<CreateJobApplicationFormModel> = React.useMemo(() => {
    if (mainUserRole === UserRole.AGENT) {
      return {
        source: JobApplicationSource.REFERRAL,
        note: `Referral ${user.name}`,
        applicationType: JobApplicationType.SALES,
      };
    }

    return {};
  }, [mainUserRole, user]);

  const onSubmit = React.useCallback(async (values: CreateJobApplicationFormModel) => {
    try {
      setIsSaving(true);

      const newApplicant: Applicant = {
        name: values.name,
        email: values.email || undefined,
        phoneNumber: values.phoneNumber || undefined,
        linkedIn: values.linkedIn || undefined,
        missedCalls: {
          counter: 0,
          list: [],
        },
        optIns: [{
          type: OptInType.SERVICE,
          sourceType: OptInSource.BACKOFFICE,
          sourceValue: 'dove.it',
          givenAt: new Date().toISOString(),
        }],
      };

      const savedApplicant = await createApplicant(newApplicant);

      const newJobApplication: JobApplication = {
        applicationType: values.applicationType!,
        applicantId: savedApplicant.id!,
        jobPositionId: values.jobPosition!.id,
        notes: values.note ? [{
          date: new Date().toISOString(),
          role: mainUserRole!,
          author: user.name,
          text: values.note,
        }] : [],
        source: values.source || undefined,
        experience: values.experience || undefined,
        status: JobApplicationStatus.IN_PROGRESS,
        provinces: values.provinces || [],
        isEmployed: booleanStringToBool(values.isEmployed),
        hasLicense: booleanStringToBool(values.hasLicense),
        licenseId: values.licenseId || undefined,
        referralEmail: mainUserRole === UserRole.AGENT ? user.email : undefined,
      };

      const savedJobApplication = await createJobApplication(newJobApplication);

      setIsSaving(false);
      addSuccess(JOB_APPLICATION_WITH_APPLICANT_SUCCESS_MESSAGE.replace('__LINK__', `/job-applications/${savedJobApplication.id}`), 0);
      onSuccess?.(savedApplicant, savedJobApplication);
    } catch (err) {
      setIsSaving(false);
      addError(JOB_APPLICATION_WITH_APPLICANT_ERROR_MESSAGE);
    }
  }, [addError, addSuccess, mainUserRole, onSuccess, user.email, user.name]);

  return (
    <CreateJobApplicationForm
      innerRef={formRef}
      initialValues={initialValues}
      onSubmit={onSubmit}
      loading={isSaving}
      disabledFields={{
        applicationType: mainUserRole === UserRole.AGENT,
        source: mainUserRole === UserRole.AGENT,
      }}
    />
  );
};

export default CreateJobApplicationWithApplicant;
